import ArticleIcon from "@mui/icons-material/Article";
import ContactMailIcon from "@mui/icons-material/ContactMail";
import FacebookIcon from "@mui/icons-material/Facebook";
import HomeIcon from "@mui/icons-material/Home";
import InstagramIcon from "@mui/icons-material/Instagram";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import PolicyIcon from "@mui/icons-material/Policy";
import XIcon from "@mui/icons-material/X";
import {
  Box,
  Container,
  Stack,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import { motion } from "framer-motion";
import viewpaths from "global/constants/ViewPathConstants";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

const Footer = () => {
  const history = useHistory();
  const theme = useTheme();
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();

  const pageMenu = [
    {
      label: `${t("home")}`,
      url: viewpaths.landingViewPath,
      icon: HomeIcon,
    },
    {
      label: `${t("blogs")}`,
      url: viewpaths.blogsViewPath,
      icon: ArticleIcon,
    },
    {
      label: `${t("contact")}`,
      url: viewpaths.contactViewPath,
      icon: ContactMailIcon,
    },
  ];

  const socialMedia = [
    {
      label: "Facebook",
      icon: FacebookIcon,
      url: "https://www.facebook.com/OfficialPanaceaProject/",
    },
    {
      label: "Instagram",
      icon: InstagramIcon,
      url: "https://www.instagram.com/OfficialPanaceaProject/",
    },
    {
      label: "X",
      icon: XIcon,
      url: "https://x.com/PanaceaProject1",
    },
    {
      label: "LinkedIn",
      icon: LinkedInIcon,
      url: "https://www.linkedin.com/company/panaceaproject?trk=similar-pages&originalSubdomain=be",
    },
  ];

  const legal = [
    {
      label: `${t("privacyPolicy")}`,
      url: viewpaths.privacyPolicyViewPath,
      icon: PolicyIcon,
    },
  ];

  const getFooter = () => {
    return (
      <Box>
        <style>
          {`
            @keyframes floatOrb1 {
              0%, 100% { transform: translate(0, 0) scale(1); }
              33% { transform: translate(30px, -20px) scale(1.1); }
              66% { transform: translate(-20px, 15px) scale(0.9); }
            }
            @keyframes floatOrb2 {
              0%, 100% { transform: translate(0, 0) scale(1); }
              50% { transform: translate(-25px, 25px) scale(1.15); }
            }
            @keyframes floatOrb3 {
              0%, 100% { transform: translate(0, 0) scale(1); }
              50% { transform: translate(20px, -30px) scale(0.85); }
            }
            @keyframes rotateGradient {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
            @keyframes pulseGlow {
              0%, 100% { opacity: 0.4; transform: scale(1); }
              50% { opacity: 0.7; transform: scale(1.05); }
            }
            @keyframes shimmer {
              0% { background-position: -200% center; }
              100% { background-position: 200% center; }
            }
            @keyframes geometricFloat {
              0%, 100% { transform: translateY(0) rotate(0deg); }
              50% { transform: translateY(-15px) rotate(5deg); }
            }
            @keyframes particleDrift {
              0% { transform: translate(0, 0); opacity: 0.3; }
              50% { transform: translate(20px, -30px); opacity: 0.6; }
              100% { transform: translate(-15px, 20px); opacity: 0.3; }
            }
          `}
        </style>
        <Box
          sx={{
            position: "relative",
            width: "100%",
            backgroundColor:
              theme.palette.mode === "dark"
                ? "rgba(27, 27, 27, 0.98)"
                : "rgba(255, 255, 255, 0.98)",
            borderTop: `2px solid ${alpha("#f8481c", 0.2)}`,
            overflow: "hidden",
            zIndex: 2,
            backdropFilter: "blur(10px)",
          }}
        >
          {/* Enhanced Creative Background Elements */}
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              overflow: "hidden",
              pointerEvents: "none",
              zIndex: 0,
            }}
          >
            {/* Animated Gradient Orbs */}
            <motion.div
              animate={{
                x: [0, 30, -20, 0],
                y: [0, -20, 15, 0],
                scale: [1, 1.1, 0.9, 1],
              }}
              transition={{
                duration: 20,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  top: "-40%",
                  right: "-8%",
                  width: "350px",
                  height: "350px",
                  borderRadius: "50%",
                  background: `radial-gradient(circle, ${alpha(
                    "#f8481c",
                    0.12,
                  )} 0%, ${alpha("#f8481c", 0.05)} 40%, transparent 70%)`,
                  filter: "blur(70px)",
                }}
              />
            </motion.div>
            <motion.div
              animate={{
                x: [0, -25, 0],
                y: [0, 25, 0],
                scale: [1, 1.15, 1],
              }}
              transition={{
                duration: 15,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  bottom: "-25%",
                  left: "-4%",
                  width: "250px",
                  height: "250px",
                  borderRadius: "50%",
                  background: `radial-gradient(circle, ${alpha(
                    "#f8481c",
                    0.1,
                  )} 0%, ${alpha("#f8481c", 0.04)} 40%, transparent 70%)`,
                  filter: "blur(60px)",
                }}
              />
            </motion.div>
            <motion.div
              animate={{
                x: [0, 20, 0],
                y: [0, -30, 0],
                scale: [1, 0.85, 1],
              }}
              transition={{
                duration: 18,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  top: "20%",
                  left: "50%",
                  width: "180px",
                  height: "180px",
                  borderRadius: "50%",
                  background: `radial-gradient(circle, ${alpha(
                    "#f8481c",
                    0.08,
                  )} 0%, transparent 60%)`,
                  filter: "blur(50px)",
                }}
              />
            </motion.div>

            {/* Animated Gradient Mesh */}
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: `radial-gradient(circle at 20% 30%, ${alpha(
                  "#f8481c",
                  0.06,
                )} 0%, transparent 50%),
                             radial-gradient(circle at 80% 70%, ${alpha(
                               "#f8481c",
                               0.05,
                             )} 0%, transparent 50%),
                             radial-gradient(circle at 50% 50%, ${alpha(
                               "#f8481c",
                               0.04,
                             )} 0%, transparent 50%)`,
                opacity: 0.8,
                animation: "pulseGlow 8s ease-in-out infinite",
              }}
            />

            {/* Animated Grid Pattern */}
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundImage: `linear-gradient(${alpha(
                  theme.palette.divider,
                  0.04,
                )} 1px, transparent 1px), 
                                   linear-gradient(90deg, ${alpha(
                                     theme.palette.divider,
                                     0.04,
                                   )} 1px, transparent 1px)`,
                backgroundSize: "50px 50px",
                opacity: 0.6,
                backgroundPosition: "0 0",
                animation: "shimmer 20s linear infinite",
              }}
            />

            {/* Floating Geometric Shapes */}
            <motion.div
              animate={{
                y: [0, -15, 0],
                rotate: [0, 5, 0],
              }}
              transition={{
                duration: 6,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  top: "15%",
                  right: "15%",
                  width: "80px",
                  height: "80px",
                  background: `linear-gradient(135deg, ${alpha(
                    "#f8481c",
                    0.08,
                  )} 0%, transparent 100%)`,
                  borderRadius: "10% 40% 10% 40%",
                  filter: "blur(2px)",
                  transform: "rotate(45deg)",
                }}
              />
            </motion.div>
            <motion.div
              animate={{
                y: [0, 12, 0],
                rotate: [0, -8, 0],
              }}
              transition={{
                duration: 7,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 0.5,
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  bottom: "20%",
                  right: "25%",
                  width: "60px",
                  height: "60px",
                  background: `linear-gradient(45deg, ${alpha(
                    "#f8481c",
                    0.06,
                  )} 0%, transparent 100%)`,
                  borderRadius: "50%",
                  clipPath: "polygon(50% 0%, 0% 100%, 100% 100%)",
                  filter: "blur(1.5px)",
                }}
              />
            </motion.div>

            {/* Floating Particles */}
            {[...Array(6)].map((_, i) => (
              <motion.div
                key={i}
                animate={{
                  x: [0, Math.random() * 40 - 20, 0],
                  y: [0, Math.random() * 40 - 20, 0],
                  opacity: [0.3, 0.6, 0.3],
                }}
                transition={{
                  duration: 10 + i * 2,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: i * 0.5,
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    top: `${15 + i * 12}%`,
                    left: `${10 + i * 15}%`,
                    width: "4px",
                    height: "4px",
                    borderRadius: "50%",
                    background: alpha("#f8481c", 0.4),
                    boxShadow: `0 0 8px ${alpha("#f8481c", 0.3)}`,
                  }}
                />
              </motion.div>
            ))}

            {/* Animated Accent Lines */}
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: "10%",
                width: "2px",
                height: "100%",
                background: `linear-gradient(180deg, transparent 0%, ${alpha(
                  "#f8481c",
                  0.2,
                )} 50%, transparent 100%)`,
                opacity: 0.6,
              }}
            />
            <Box
              sx={{
                position: "absolute",
                top: 0,
                right: "15%",
                width: "2px",
                height: "100%",
                background: `linear-gradient(180deg, transparent 0%, ${alpha(
                  "#f8481c",
                  0.15,
                )} 50%, transparent 100%)`,
                opacity: 0.5,
              }}
            />
          </Box>

          <Container
            maxWidth="lg"
            sx={{ position: "relative", zIndex: 1, py: { xs: 2.5, md: 3 } }}
          >
            {/* Main Content - Horizontal Compact Layout */}
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={{ xs: 2.5, md: 4 }}
              alignItems={{ xs: "center", md: "center" }}
              justifyContent="space-between"
            >
              {/* Navigation Links with Icons */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={{ xs: 1.5, sm: 3 }}
                  alignItems="center"
                  sx={{
                    position: "relative",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      left: { xs: "50%", sm: "-20px" },
                      top: { xs: "-12px", sm: "50%" },
                      transform: {
                        xs: "translateX(-50%)",
                        sm: "translateY(-50%)",
                      },
                      width: { xs: "60px", sm: "2px" },
                      height: { xs: "2px", sm: "60px" },
                      background: `linear-gradient(90deg, transparent 0%, ${alpha(
                        "#f8481c",
                        0.3,
                      )} 50%, transparent 100%)`,
                      display: { xs: "block", sm: "none" },
                    },
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      left: "-20px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      width: "2px",
                      height: "60px",
                      background: `linear-gradient(180deg, transparent 0%, ${alpha(
                        "#f8481c",
                        0.3,
                      )} 50%, transparent 100%)`,
                      display: { xs: "none", sm: "block" },
                    },
                  }}
                >
                  {pageMenu.map((page: any, index: number) => {
                    const IconComponent = page.icon;
                    return (
                      <motion.div
                        key={page.url}
                        whileHover={{ scale: 1.08, y: -3 }}
                        whileTap={{ scale: 0.95 }}
                        transition={{ duration: 0.2 }}
                      >
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          onClick={() => history.push(page.url)}
                          sx={{
                            cursor: "pointer",
                            px: 2.5,
                            py: 1.2,
                            borderRadius: "7px",
                            background: `linear-gradient(135deg, ${alpha(
                              "#f8481c",
                              0.08,
                            )} 0%, ${alpha("#f8481c", 0.03)} 100%)`,
                            border: `1.5px solid ${alpha("#f8481c", 0.2)}`,
                            position: "relative",
                            overflow: "hidden",
                            transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                            "&::before": {
                              content: '""',
                              position: "absolute",
                              top: 0,
                              left: "-100%",
                              width: "100%",
                              height: "100%",
                              background: `linear-gradient(90deg, transparent, ${alpha(
                                "#f8481c",
                                0.15,
                              )}, transparent)`,
                              transition: "left 0.5s ease",
                            },
                            "&:hover": {
                              background: `linear-gradient(135deg, ${alpha(
                                "#f8481c",
                                0.15,
                              )} 0%, ${alpha("#f8481c", 0.08)} 100%)`,
                              border: `1.5px solid ${alpha("#f8481c", 0.4)}`,
                              boxShadow: `0 6px 20px ${alpha(
                                "#f8481c",
                                0.3,
                              )}, 0 0 30px ${alpha("#f8481c", 0.15)}`,
                              transform: "translateY(-3px) scale(1.02)",
                              "&::before": {
                                left: "100%",
                              },
                            },
                          }}
                        >
                          <IconComponent
                            sx={{
                              fontSize: "18px",
                              color: "#f8481c",
                              transition: "all 0.4s ease",
                              position: "relative",
                              zIndex: 1,
                            }}
                          />
                          <Typography
                            sx={{
                              fontSize: { xs: "13px", md: "14px" },
                              fontWeight: 600,
                              color: theme.palette.text.primary,
                              transition: "all 0.4s ease",
                              position: "relative",
                              zIndex: 1,
                              "&:hover": {
                                color: "#f8481c",
                              },
                            }}
                          >
                            {page.label}
                          </Typography>
                        </Stack>
                      </motion.div>
                    );
                  })}
                  {legal.map((page: any) => {
                    const IconComponent = page.icon;
                    return (
                      <motion.div
                        key={page.url}
                        whileHover={{ scale: 1.08, y: -3 }}
                        whileTap={{ scale: 0.95 }}
                        transition={{ duration: 0.2 }}
                      >
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          onClick={() => history.push(page.url)}
                          sx={{
                            cursor: "pointer",
                            px: 2,
                            py: 1,
                            borderRadius: "6px",
                            background: alpha("#f8481c", 0.05),
                            border: `1px solid ${alpha("#f8481c", 0.15)}`,
                            transition: "all 0.3s ease",
                            "&:hover": {
                              background: alpha("#f8481c", 0.12),
                              border: `1px solid ${alpha("#f8481c", 0.3)}`,
                              boxShadow: `0 4px 12px ${alpha("#f8481c", 0.2)}`,
                              transform: "translateY(-2px)",
                            },
                          }}
                        >
                          <IconComponent
                            sx={{
                              fontSize: "18px",
                              color: "#f8481c",
                              transition: "all 0.3s ease",
                            }}
                          />
                          <Typography
                            sx={{
                              fontSize: { xs: "13px", md: "14px" },
                              fontWeight: 600,
                              color: theme.palette.text.primary,
                              transition: "all 0.3s ease",
                              "&:hover": {
                                color: "#f8481c",
                              },
                            }}
                          >
                            {page.label}
                          </Typography>
                        </Stack>
                      </motion.div>
                    );
                  })}
                </Stack>
              </motion.div>

              {/* Enhanced Decorative Divider */}
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.15 }}
              >
                <Box
                  sx={{
                    display: { xs: "none", md: "block" },
                    width: "3px",
                    height: "50px",
                    position: "relative",
                    background: `linear-gradient(180deg, transparent 0%, ${alpha(
                      "#f8481c",
                      0.5,
                    )} 30%, ${alpha("#f8481c", 0.7)} 50%, ${alpha(
                      "#f8481c",
                      0.5,
                    )} 70%, transparent 100%)`,
                    borderRadius: "3px",
                    boxShadow: `0 0 15px ${alpha("#f8481c", 0.4)}`,
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      width: "8px",
                      height: "8px",
                      borderRadius: "50%",
                      background: alpha("#f8481c", 0.6),
                      boxShadow: `0 0 12px ${alpha("#f8481c", 0.5)}`,
                    },
                  }}
                />
              </motion.div>

              {/* Social Media Icons - Enhanced */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.1 }}
              >
                <Stack
                  direction="row"
                  spacing={1.5}
                  alignItems="center"
                  sx={{
                    position: "relative",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      right: "-20px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      width: "2px",
                      height: "60px",
                      background: `linear-gradient(180deg, transparent 0%, ${alpha(
                        "#f8481c",
                        0.3,
                      )} 50%, transparent 100%)`,
                      display: { xs: "none", md: "block" },
                    },
                  }}
                >
                  {socialMedia.map((social: any, index: number) => {
                    const IconComponent = social.icon;
                    return (
                      <motion.div
                        key={social.label}
                        whileHover={{ scale: 1.2, rotate: 10, y: -5 }}
                        whileTap={{ scale: 0.9 }}
                        transition={{ duration: 0.2 }}
                      >
                        <Box
                          onClick={() => window.open(social.url, "_blank")}
                          sx={{
                            width: { xs: "42px", md: "46px" },
                            height: { xs: "42px", md: "46px" },
                            borderRadius: "50%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                            background: `linear-gradient(135deg, ${alpha(
                              "#f8481c",
                              0.18,
                            )} 0%, ${alpha("#f8481c", 0.1)} 50%, ${alpha(
                              "#f8481c",
                              0.05,
                            )} 100%)`,
                            border: `2.5px solid ${alpha("#f8481c", 0.35)}`,
                            position: "relative",
                            transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                            overflow: "hidden",
                            "&::before": {
                              content: '""',
                              position: "absolute",
                              inset: "-3px",
                              borderRadius: "50%",
                              padding: "3px",
                              background: `conic-gradient(from 0deg, ${alpha(
                                "#f8481c",
                                0.5,
                              )}, ${alpha("#f8481c", 0.2)}, ${alpha(
                                "#f8481c",
                                0.5,
                              )})`,
                              WebkitMask:
                                "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
                              WebkitMaskComposite: "xor",
                              maskComposite: "exclude",
                              opacity: 0,
                              transition: "opacity 0.4s ease",
                              animation: "rotateGradient 3s linear infinite",
                            },
                            "&::after": {
                              content: '""',
                              position: "absolute",
                              inset: "2px",
                              borderRadius: "50%",
                              background: `radial-gradient(circle, ${alpha(
                                "#f8481c",
                                0.1,
                              )} 0%, transparent 70%)`,
                              opacity: 0,
                              transition: "opacity 0.4s ease",
                            },
                            "&:hover": {
                              background: `linear-gradient(135deg, ${alpha(
                                "#f8481c",
                                0.3,
                              )} 0%, ${alpha("#f8481c", 0.2)} 50%, ${alpha(
                                "#f8481c",
                                0.15,
                              )} 100%)`,
                              border: `2.5px solid ${alpha("#f8481c", 0.6)}`,
                              boxShadow: `0 0 25px ${alpha(
                                "#f8481c",
                                0.6,
                              )}, 0 6px 20px ${alpha(
                                "#f8481c",
                                0.4,
                              )}, inset 0 0 20px ${alpha("#f8481c", 0.1)}`,
                              transform: "translateY(-4px) scale(1.08)",
                              "&::before": {
                                opacity: 1,
                              },
                              "&::after": {
                                opacity: 1,
                              },
                            },
                          }}
                        >
                          <IconComponent
                            sx={{
                              fontSize: { xs: "20px", md: "22px" },
                              color: "#f8481c",
                              transition: "all 0.3s ease",
                              position: "relative",
                              zIndex: 1,
                            }}
                          />
                        </Box>
                      </motion.div>
                    );
                  })}
                </Stack>
              </motion.div>
            </Stack>

            {/* Enhanced Copyright Section */}
            <Box
              sx={{
                mt: { xs: 2.5, md: 3 },
                pt: { xs: 2, md: 2.5 },
                borderTop: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
                position: "relative",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: "50%",
                  transform: "translateX(-50%)",
                  width: "150px",
                  height: "3px",
                  background: `linear-gradient(90deg, transparent 0%, ${alpha(
                    "#f8481c",
                    0.4,
                  )} 20%, ${alpha("#f8481c", 0.7)} 50%, ${alpha(
                    "#f8481c",
                    0.4,
                  )} 80%, transparent 100%)`,
                  borderRadius: "3px",
                  boxShadow: `0 0 10px ${alpha("#f8481c", 0.3)}`,
                },
                "&::after": {
                  content: '""',
                  position: "absolute",
                  top: "-1px",
                  left: "50%",
                  transform: "translateX(-50%)",
                  width: "8px",
                  height: "8px",
                  borderRadius: "50%",
                  background: alpha("#f8481c", 0.6),
                  boxShadow: `0 0 12px ${alpha("#f8481c", 0.5)}`,
                },
              }}
            >
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={{ xs: 0.5, sm: 1.5 }}
                  alignItems="center"
                  justifyContent="center"
                >
                  <Typography
                    sx={{
                      fontSize: { xs: "11px", md: "12px" },
                      color: theme.palette.text.secondary,
                      fontWeight: 400,
                    }}
                  >
                    &copy; {currentYear} Panacea Insights
                  </Typography>
                  <Box
                    sx={{
                      display: { xs: "none", sm: "block" },
                      width: "3px",
                      height: "3px",
                      borderRadius: "50%",
                      background: alpha("#f8481c", 0.5),
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: { xs: "11px", md: "12px" },
                      color: theme.palette.text.secondary,
                      fontWeight: 400,
                    }}
                  >
                    All rights reserved
                  </Typography>
                </Stack>
              </motion.div>
            </Box>
          </Container>
        </Box>
      </Box>
    );
  };

  return getFooter();
};

export default Footer;
